import React, { ReactNode, useState } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import { svg } from '../../assets/svg/index.js';
import { useApp } from '../../contexts/AppContext';
import { components } from '..';
import { ChevronDownIcon } from '@radix-ui/react-icons';

import { 
  Star, Shield, DollarSign, Ban, Clock, CheckCircle, TimerReset, Users, 
  Building, KeyRound, Headset, Calendar1, Scale, FileWarning 
} from 'lucide-react';

interface CoverageDetail {
  label?: string;
  value: string | number | JSX.Element;
  icon: React.ElementType;
}

export const CoverageDetails: React.FC = () => {
  const { coverage } = useApp();

  if (!coverage) {
    return <div>Loading...</div>;
  }

  // Constrói os detalhes da cobertura com base no item selecionado
  const coverageDetails: CoverageDetail[] = [
    { label: 'Nome do benefício', value: coverage.benefitName || 'N/A', icon: Star },
    { label: 'Tipo de cobertuara', value: coverage.coverageType || 'N/A', icon: Shield },
    { label: 'Quantidade de Cobertura', value: coverage.coverageAmount || 'N/A', icon: DollarSign },
    { label: 'Limite de uso', value: coverage.usageLimit || 'No limit', icon: TimerReset },
    { label: 'Período de espera', value: coverage.waitingPeriod || '0 days', icon: Clock  },
    {
      label: 'Serviços incluídos',
      value: coverage.includedServices?.join(', ') || 'N/A',
      icon: CheckCircle,
    },
    { label: 'Exclusões', value: coverage.exclusions?.join(', ') || 'None', icon: Ban},
    {
      label: 'Beneficiários elegíveis',
      value: coverage.eligibleBeneficiaries || 'N/A',
      icon: Users ,
    },
    {
      value: (
        <Accordion.Root type="single" collapsible style={{ width: '100%' }}>
          <AccordionItem value="provider-details" >
            <AccordionTrigger>Detalhes do provedor</AccordionTrigger>
            <AccordionContent>
              <div style={{ marginBottom: '10px' }}>
                <strong>Type:</strong> {coverage.provider?.type || 'N/A'}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Name:</strong> {coverage.provider?.name || 'N/A'}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Phone:</strong> {coverage.provider?.contact.phone || 'N/A'}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Email:</strong> {coverage.provider?.contact.email || 'N/A'}
              </div>
              <div>
                <strong>Website:</strong>{' '}
                <a href={coverage.provider?.contact.website} target="_blank" rel="noopener noreferrer">
                  {coverage.provider?.contact.website || 'N/A'}
                </a>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion.Root>
      ),
      icon: Building,
    },
    {
      label: 'Processo de ativação',
      value: coverage.activationProcess || 'N/A',
      icon: KeyRound,
    },
    {
      value: (
        <Accordion.Root type="single" collapsible style={{ width: '100%' }}>
          <AccordionItem value="support-contact">
            <AccordionTrigger>Contato de suporte</AccordionTrigger>
            <AccordionContent>
              <div style={{ marginBottom: '10px' }}>
                <strong>Phone:</strong> {coverage.supportContact?.phone || 'N/A'}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Email:</strong> {coverage.supportContact?.email || 'N/A'}
              </div>
              <div>
                <strong>App Link:</strong>{' '}
                <a href={coverage.supportContact?.appLink} target="_blank" rel="noopener noreferrer">
                  {coverage.supportContact?.appLink || 'N/A'}
                </a>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion.Root>
      ),
      icon: Headset,
    },
    {
      label: 'Duração do benefício',
      value: coverage.benefitDuration || 'N/A',
      icon: Calendar1,
    },
    {
      label: 'Regras de ajuste',
      value: coverage.adjustmentRules || 'N/A',
      icon: Scale,
    },
    {
      value: (
        <Accordion.Root type="single" collapsible style={{ width: '100%' }}>
          <AccordionItem value="cancellation-policy">
            <AccordionTrigger>Política de Cancelamento</AccordionTrigger>
            <AccordionContent>
              <div style={{ marginBottom: '10px' }}>
                <strong>Terms:</strong> {coverage.cancellationPolicy?.terms || 'N/A'}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Penalty:</strong> {coverage.cancellationPolicy?.penalty || 'N/A'}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion.Root>
      ),
      icon: FileWarning,
    },
  ];

  const renderHeader = (): JSX.Element => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        backgroundImage: "url('https://cdn.usegalileo.ai/stability/9243e26e-1377-46e0-af00-b4c4a68d2ab2.png')",
        height: '120px',
        borderRadius: '12px 12px 0 0',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    ></div>
  );

const renderContent = (): JSX.Element => (
  <div
    style={{
      paddingLeft: 10,
      paddingRight: 10,
      marginBottom: '60px', // Espaço reservado para o botão fixo
    }}
  >
    <div
      style={{
        textAlign: 'left',
        color: '#0e141b',
        fontSize: '22px',
        fontWeight: 'bold',
        padding: '12px 0 5px 0',
      }}
    >
      Detalhes da cobertura
    </div>
    <div>
      {coverageDetails.map((detail: CoverageDetail, index: number) => (
        <div
          key={index}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            borderRadius: '8px',
            padding: '10px 0',
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              display: 'flex',
              justifyContent: 'center',
              alignSelf: 'flex-start',
              marginTop: '1.5px',
              marginLeft: '1px',
              alignItems: 'center',
              backgroundColor: '#d8d8d8',
              color: '#131e48',
              borderRadius: '8px',
            }}
          >
            <detail.icon size={20}/>
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                color: '#0e141b',
                fontWeight: '500',
                fontSize: '15px',
                marginBottom: 5,
              }}
            >
              {detail.label}
            </div>
            <div
              style={{
                color: '#4e7297',
                fontSize: '12px',
              }}
            >
              {detail.value}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

  const renderButton = (): JSX.Element => (
    <div
      style={{
        position: 'fixed', // Fixa o botão no fundo
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#ffffff', // Fundo para o botão
        padding: '0px', // Espaçamento ao redor do botão

      }}
    >
      <components.Button
        title="Solicitar Atendimento"
        containerStyle={{
          width: '100%',
          color: '131e48',
          fontWeight: 'bold',
          textAlign: 'center',
          padding: '0px 10px 10px 10px',
          borderRadius: '12px',
          border: 'none', // Remover borda
          cursor: 'pointer',
        }}
      />
    </div>
  );

  return (
    <div>
      <button className="flex h-5 w-full items-center justify-center pt-2">
        <div className="h-1 w-9 rounded-full bg-gray-300"></div>
      </button>
      {renderHeader()}
      {renderContent()}
      {renderButton()}
    </div>
  );
};

interface AccordionItemProps {
  children: ReactNode;
  value: string;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ children, value }) => (
  <Accordion.Item value={value}>{children}</Accordion.Item>
);

interface AccordionTriggerProps {
  children: ReactNode;
}

const AccordionTrigger = React.forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  ({ children, ...props }, ref) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleToggle = () => setIsOpen((prev) => !prev);

    return (
      <Accordion.Header style={{ display: 'flex', justifyContent: 'center' }}>
        <Accordion.Trigger
          ref={ref}
          onClick={handleToggle}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            padding: '0px 0px',
            fontSize: '15px',
            fontWeight: '500',
            color: '#0e141b',
            textAlign: 'center',
            border: 'none',
            outline: 'none',
            cursor: 'pointer',
            backgroundColor: '#ffffff',
          }}
          {...props}
        >
          <span style={{ marginRight: 'auto' }}>{children}</span>
          <ChevronDownIcon
            style={{
              transition: 'transform 0.3s ease',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              color: '#000000',
              marginRight: '5px',
            }}
          />
        </Accordion.Trigger>
      </Accordion.Header>
    );
  }
);

AccordionTrigger.displayName = 'AccordionTrigger';

interface AccordionContentProps {
  children: ReactNode;
}

const AccordionContent = React.forwardRef<HTMLDivElement, AccordionContentProps>(
  ({ children, ...props }, ref) => (
    <Accordion.Content
      ref={ref}
      style={{
        overflow: 'hidden',
        color: '#4e7297',
        fontSize: '12px',
        padding: '0px 0px',
        transition: 'max-height 0.3s ease',
      }}
      {...props}
    >
      <div style={{ padding: '10px 0' }}>{children}</div>
    </Accordion.Content>
  )
);

AccordionContent.displayName = 'AccordionContent';
